import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Col,
  Form,
  Input,
  Layout,
  Modal,
  Row,
  Select,
  Typography,
  message,
} from 'antd';
import { startCase } from 'lodash';

import PdButton from 'components/pd-button';
import PdUploader from 'components/pd-uploader';
import { TableList } from 'components/table-list';

import CategorySectionApi from 'services/category_section';
import CategoryApi from 'services/category';

import { errHandling } from 'utils';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Text, Title } = Typography;
const { confirm } = Modal;

const FIELDS = {
  IMAGE: 'image',
  COLLECTIONS: 'categories_and_sections_attributes',
  TITLE: 'title',
  TITLE_BAHASA: 'title_idn',
};

export function CategorySectionCreateEdit() {
  const [form] = Form.useForm();
  const [categoryChildrenList, setCategoryChildrenList] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedCategory, setSelectedCategory] = useState(undefined);
  const [selectedCategoryIcon, setSelectedCategoryIcon] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [value, setValue] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const sectionId = urlParams.get('id');

  const formItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    labelAlign: 'left',
  };

  const displayStatus = (collection) => (
    <Col span={15}>
      <div className="button-table">
        <Row>{startCase(collection.status)}</Row>
      </div>
    </Col>
  );

  const buttonAction = ({ id, status } = {}) => (
    <Col span={15}>
      <div className="button-table">
        <Row>
          <Col span={12} className="button">
            {status === 'active' ? (
              <PdButton
                className="height-32"
                type="danger"
                outline
                full
                onClick={handleHide.bind(this, id)}>
                Deactivate
              </PdButton>
            ) : (
              <PdButton
                type="primary"
                outline
                full
                className="height-32"
                onClick={handleRestore.bind(this, id)}>
                Reactivate
              </PdButton>
            )}
          </Col>
          <Col span={12} className="button">
            {status === 'active' ? (
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={handleEdit.bind(this, id)}>
                Edit
              </PdButton>
            ) : (
              <PdButton
                onClick={handleDelete.bind(this, id)}
                type="danger"
                full
                className="height-32">
                Delete
              </PdButton>
            )}
          </Col>
        </Row>
      </div>
    </Col>
  );

  const columns = [
    {
      name: 'Categories',
      selector: row => row.title,
      center: true,
    },
    {
      name: 'Status',
      cell: displayStatus,
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      center: true,
    },
  ];

  function handleEdit(categoryId) {
    const { icon = '' } =
      value[FIELDS.COLLECTIONS].find(({ id }) => +id === +categoryId) || {};
    setSelectedCategoryIcon(icon);
    setSelectedCategory(categoryId);
    setModalType('edit');
    setShowModal(true);
  }

  function handleCloseModal() {
    setModalType('add');
    setSelectedCategoryIcon('');
    setSelectedCategory(undefined);
    setShowModal(false);
  }

  function handleOnChangeCategory(selectedId) {
    const { icon = '' } =
      categoryChildrenList.find(({ id }) => +id === +selectedId) || {};
    setSelectedCategoryIcon(icon);
    setSelectedCategory(selectedId);
  }

  function handleSubmitCategory() {
    if (modalType === 'edit') {
      setValue((prev) => {
        const { [FIELDS.COLLECTIONS]: dataArr = [] } = { ...prev };
        const editResult = dataArr.map((category = {}) => {
          const { id } = category;
          if (+id === +selectedCategory) {
            return { ...category, icon: selectedCategoryIcon };
          } else {
            return { ...category };
          }
        });
        return { ...prev, [FIELDS.COLLECTIONS]: editResult };
      });
    } else {
      const data = categoryChildrenList.find(
        ({ id }) => +id === +selectedCategory
      );
      const isAlreadyAdded = value[FIELDS.COLLECTIONS].find(
        ({ id }) => +id === +selectedCategory
      );
      if (isAlreadyAdded) {
        message.error('Category has already added');
      } else {
        setValue((prev) => ({
          ...prev,
          [FIELDS.COLLECTIONS]: [...prev[FIELDS.COLLECTIONS], data],
        }));
      }
    }
    handleCloseModal();
  }

  function handleDelete(categoryId) {
    confirm({
      title: 'Are you sure to delete this category?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        setValue((prev) => {
          const newValue = [...prev[FIELDS.COLLECTIONS]].filter(
            ({ id }) => +categoryId !== +id
          );
          return { ...prev, [FIELDS.COLLECTIONS]: newValue };
        });
      },
    });
  }

  async function hideCategory(id) {
    try {
      if (id) {
        await CategoryApi.hideCategory(id);
        setValue((prev) => {
          const newValue = value[FIELDS.COLLECTIONS].map((category = {}) => {
            const { id: categoryId, status } = category;
            return {
              ...category,
              status: categoryId === id ? 'inactive' : status,
            };
          });
          return { ...prev, [FIELDS.COLLECTIONS]: newValue };
        });
        message.success('Successfully removed category');
      }
    } catch (err) {
      errHandling(err);
    }
  }

  function handleHide(id) {
    confirm({
      title: 'Are you sure you want to hide this collection?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        hideCategory(id);
      },
      onCancel() {},
    });
  }

  async function handleRestore(id) {
    try {
      if (id) {
        await CategoryApi.restoreCategory(id);
        message.success('Successfully restored');
        setValue((prev) => {
          const newValue = value[FIELDS.COLLECTIONS].map((category = {}) => {
            const { id: categoryId, status } = category;
            return {
              ...category,
              status: categoryId === id ? 'active' : status,
            };
          });
          return { ...prev, [FIELDS.COLLECTIONS]: newValue };
        });
      }
    } catch (err) {
      errHandling(err);
    }
  }

  async function onFinish(values) {
    setLoadingButton(true);
    const collections = value[FIELDS.COLLECTIONS].map(({ icon, id }) => ({
      category_id: id,
      icon,
    }));
    const updateCategoriesPayload = value[FIELDS.COLLECTIONS].map(
      ({ icon, id }) => ({
        id,
        icon,
      })
    );
    const payload = {
      ...values,
      [FIELDS.COLLECTIONS]: collections,
    };
    try {
      await CategoryApi.updateCategory(updateCategoriesPayload);
      await CategorySectionApi.updateCategorySection(sectionId, payload);
      message.success('Successfully edit section');
      navigate('/category-sections');
    } catch (err) {
      errHandling(err);
    } finally {
      setLoadingButton(false);
    }
  }

  async function fetchCategorySectionDetail() {
    setLoadingPage(true);
    try {
      const {
        data: { data = {} },
      } = await CategorySectionApi.getCategorySection(sectionId);
      const { image, categories = [], title, title_idn } = data;

      setValue({
        [FIELDS.IMAGE]: image,
        [FIELDS.COLLECTIONS]: categories,
        [FIELDS.TITLE]: title,
        [FIELDS.TITLE_BAHASA]: title_idn,
      });
      form.setFieldsValue({
        [FIELDS.TITLE]: title,
        [FIELDS.TITLE_BAHASA]: title_idn,
        [FIELDS.IMAGE]: image,
      });
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingPage(false);
    }
  }

  async function fetchCategoryChildren() {
    try {
      const { data: { data = [] } = {} } =
        await CategorySectionApi.getCategoryChildren();
      setCategoryChildrenList(data);
    } catch (error) {
      errHandling(error);
    }
  }

  function handleUploadImg(imgUrl) {
    form.setFieldsValue({ [FIELDS.IMAGE]: imgUrl });
    setValue((prev) => ({ ...prev, [FIELDS.IMAGE]: imgUrl }));
  }

  useEffect(() => {
    fetchCategoryChildren();
    if (sectionId) {
      fetchCategorySectionDetail();
    } else {
      setValue((prev) => ({ ...prev, [FIELDS.COLLECTIONS]: [] }));
    }

    // eslint-disable-next-line
  }, []);

  return (
    <Layout className="main-container pd-cms-product-section">
      <Form form={form} className="pd-product-section-form" onFinish={onFinish}>
        <div className="content">
          <div className="pageTitle">
            {sectionId ? 'Edit' : 'Create'} Category Section
          </div>
          <Content className="pageContent">
            <Row gutter={[18, 36]}>
              <Col lg={16}>
                <Form.Item
                  label="Title"
                  name={FIELDS.TITLE}
                  {...formItemLayout}
                  rules={[
                    {
                      message: 'Please input section title!',
                      required: true,
                    },
                  ]}>
                  <Input placeholder="Input title here" />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4, offset: 4 }}>
                <PdButton
                  className="height-32"
                  type="primary"
                  full
                  onClick={setShowModal.bind(this, true)}>
                  Add Category
                </PdButton>
              </Col>
            </Row>
            <Row gutter={[18, 36]}>
              <Col lg={16}>
                <Form.Item
                  label="Title (Bahasa)"
                  name={FIELDS.TITLE_BAHASA}
                  {...formItemLayout}
                  rules={[
                    {
                      message: 'Please input section title (bahasa)!',
                      required: true,
                    },
                  ]}>
                  <Input placeholder="Input title in bahasa here" />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col lg={16}>
                <Form.Item
                  label={
                    <span>
                      Image (3:2){' '}
                      <Typography.Text type="secondary" strong>
                        <i>
                          Popular resolutions: 720x480 | 1152x768 | 1280x854 |
                          1440x960 | 2880x1920
                        </i>
                      </Typography.Text>
                    </span>
                  }
                  name={FIELDS.IMAGE}
                  {...formItemLayout}>
                  <PdUploader
                    alt="uploaded-img"
                    afterUpload={handleUploadImg}
                    imgUrl={value[FIELDS.IMAGE]}
                    imgClassName="category-sections"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Form.Item style={{width : '100%'}}>
                {!loadingPage && (
                  <TableList
                    columns={columns}
                    loadingOrder={loadingPage}
                    orderList={value[FIELDS.COLLECTIONS]}
                    page={1}
                    responsive={false}
                  />
                )}
              </Form.Item>
            </Row>
            <Row type="flex" justify="center">
              <Col lg={12}>
                <Form.Item>
                  <PdButton
                    className="w-full height-32"
                    full
                    htmlType="submit"
                    loading={loadingButton}
                    type="primary">
                    Submit
                  </PdButton>
                </Form.Item>
              </Col>
            </Row>
          </Content>
        </div>
      </Form>

      {/* Modal Section */}
      {showModal && (
        <Modal
          open={showModal}
          wrapClassName="pd-product-section-form-wrap-modal"
          centered
          onCancel={handleCloseModal}
          footer={false}>
          <Row type="flex" justify="center">
            <Title level={4}>{startCase(modalType)} Category</Title>
          </Row>

          {/* Select Category Section */}
          <Row className="pd-margin-top-md">
            <Text>Select Category: </Text>
          </Row>
          <Row>
            <Select
              className="w-full pd-margin-top-sm"
              filterOption={false}
              onChange={handleOnChangeCategory}
              placeholder="Select active category"
              value={selectedCategory}>
              {categoryChildrenList.map(({ id = '', title = '' }) => (
                <Option key={id} value={id}>
                  {startCase(title)}
                </Option>
              ))}
            </Select>
          </Row>

          {/* Icon Section */}
          {selectedCategory && (
            <>
              <Row className="pd-margin-top-md">
                <Text>{'Icon (50 x 50): '}</Text>
              </Row>
              <Row>
                <PdUploader
                  afterUpload={setSelectedCategoryIcon}
                  alt="category-icon"
                  imgUrl={selectedCategoryIcon}
                />
              </Row>
            </>
          )}

          <Row type="flex" justify="center" className="pd-margin-top-lg">
            <Col lg={12}>
              <PdButton
                className="w-full height-32"
                full
                loading={loadingButton}
                onClick={handleSubmitCategory}
                type="primary">
                Save
              </PdButton>
            </Col>
          </Row>
        </Modal>
      )}
    </Layout>
  );
}

export default CategorySectionCreateEdit;
